import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  Input,
  Button,
  Textarea,
  HStack,
  RadioGroup,
  Radio,
  IconButton,
  Text,
  Spacer,
  useColorMode,
  Heading,
  Flex,
  useToast,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon } from "@chakra-ui/icons";
import axiosInstance from "../../helpers/axiosInstance"; // Adjust import path for axios
import Header from "./Header";

const AdminFeedbackSurvey = () => {
  const { colorMode } = useColorMode();
  const toast = useToast();
  const [questions, setQuestions] = useState([]);
  const [newQuestionLabel, setNewQuestionLabel] = useState("");
  const [newQuestionType, setNewQuestionType] = useState("radio");
  const [newOptions, setNewOptions] = useState([""]); // For radio options
  const [newRequired, setNewRequired] = useState(false); // For required toggle
  const [heading, setHeading] = useState(""); // Survey heading
  const [subtext, setSubtext] = useState(""); // Survey subtext
  const [feedbackField, setFeedbackField] = useState(""); // Feedback field as string
  const [isEditing, setIsEditing] = useState(false); // Track if editing existing survey

  // Add a new question to the survey
  const addQuestion = () => {
    if (newQuestionLabel.trim() !== "") {
      const newQuestion = {
        type: newQuestionType,
        label: newQuestionLabel,
        required: newRequired,
        ...(newQuestionType === "radio" ? { options: [...newOptions] } : {}),
      };
      setQuestions([...questions, newQuestion]);
      setNewQuestionLabel("");
      setNewOptions([""]); // Reset options for next radio button set
      setNewRequired(false);
      setNewQuestionType("radio");
    }
  };

  // Add a new option for radio buttons
  const addOption = (index) => {
    const updatedOptions = [...newOptions];
    updatedOptions.splice(index + 1, 0, ""); // Add empty option
    setNewOptions(updatedOptions);
  };

  // Handle change for individual options
  const handleOptionChange = (value, index) => {
    const updatedOptions = [...newOptions];
    updatedOptions[index] = value;
    setNewOptions(updatedOptions);
  };

  // Delete a question from the survey
  const deleteQuestion = (index) => {
    setQuestions(questions.filter((_, qIndex) => qIndex !== index));
  };

  // Save survey logic with API call
  const saveSurvey = async () => {
    // Frontend validation: Ensure heading is present and either questions or feedbackField is provided
    if (!heading) {
      toast({
        title: "Error",
        description: "Heading is required.",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
      return;
    }
    if (questions.length === 0 && !feedbackField.trim()) {
      toast({
        title: "Error",
        description: "At least one question or feedback field must be provided.",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
      return;
    }
    // Prepare questions for payload
    const formattedQuestions = questions.map((q) => {
      const base = {
        type: q.type,
        label: q.label,
        required: q.required,
      };
      if (q.type === "radio") {
        return { ...base, options: q.options.filter((opt) => opt.trim() !== "") };
      }
      return base;
    });
    const surveyData = {
      heading,
      subtext,
      questions: formattedQuestions,
      feedbackField: feedbackField.trim(),
    };
    // console.log("surveyData", surveyData);
    try {
      // Make a request to the backend to create the survey
      const response = await axiosInstance.post("/survey/create", surveyData);
      if (response.status === 201) {
        // Reset form fields after saving
        setHeading("");
        setSubtext("");
        setQuestions([]);
        setNewQuestionLabel("");
        setNewOptions([""]);
        setFeedbackField("");
        setNewRequired(false);
        setNewQuestionType("radio");
        // Show success message from backend
        toast({
          title: "Success",
          description: response.data.message,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else if (response.status >= 400 && response.status < 500) {
        // Display backend validation error messages (client-side errors)
        toast({
          title: "Error",
          description: response.data.message || "Something went wrong.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      // Backend errors (like validation failures)
      if (error.response && error.response.data) {
        const backendErrors = error.response.data.errors || error.response.data.message;
        if (Array.isArray(backendErrors)) {
          // If backend returns multiple errors, map them and display as a list
          backendErrors.forEach((err) => {
            toast({
              title: "Error",
              description: err,
              status: "error",
              duration: 3000,
              isClosable: true,
            });
          });
        } else {
          // Single error message from backend
          toast({
            title: "Error",
            description: backendErrors,
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        }
      } else {
        // General error handling for unexpected cases
        toast({
          title: "Error",
          description: "Failed to save the survey. Please try again.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  // Fetch existing survey to enable edit mode
  useEffect(() => {
    const fetchExistingSurvey = async () => {
      try {
        const response = await axiosInstance.get("/survey/fetch");
        if (response.status === 200 && response.data && response.data.survey) {
          const survey = response.data.survey;
          setHeading(survey.heading || "");
          setSubtext(survey.subtext || "");
          setQuestions(Array.isArray(survey.questions) ? survey.questions : []);
          setFeedbackField(survey.feedbackField || "");
          setIsEditing(true);
        }
      } catch (error) {
        // If no survey is found (404), remain in create mode silently
      }
    };
    fetchExistingSurvey();
  }, []);

  // Update survey logic with API call
  const updateSurvey = async () => {
    if (!heading) {
      toast({
        title: "Error",
        description: "Heading is required.",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
      return;
    }
    if (questions.length === 0 && !feedbackField.trim()) {
      toast({
        title: "Error",
        description: "At least one question or feedback field must be provided.",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    const formattedQuestions = questions.map((q) => {
      const base = {
        type: q.type,
        label: q.label,
        required: q.required,
      };
      if (q.type === "radio") {
        return { ...base, options: (q.options || []).filter((opt) => (opt || "").trim() !== "") };
      }
      if (q.type === "image") {
        return { ...base }; // image type only needs label/required
      }
      return base;
    });

    const payload = {
      heading,
      subtext,
      questions: formattedQuestions,
      feedbackField: feedbackField.trim(),
    };

    try {
      const response = await axiosInstance.put("/survey/edit", payload);
      if (response.status === 200) {
        toast({
          title: "Success",
          description: response.data.message || "Survey updated successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        setIsEditing(true);
      } else if (response.status >= 400 && response.status < 500) {
        toast({
          title: "Error",
          description: response.data.message || "Something went wrong.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      const backendErrors = error.response && (error.response.data.errors || error.response.data.message);
      if (Array.isArray(backendErrors)) {
        backendErrors.forEach((err) => {
          toast({
            title: "Error",
            description: err,
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        });
      } else {
        toast({
          title: "Error",
          description: backendErrors || "Failed to update the survey. Please try again.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  return (
    <Box h="100vh">
      <Header/>
      {/* Main survey creation content */}
      <Box h="90vh" overflowY="auto">
        <VStack
          spacing={4}
          align="flex-start"
          justifyContent="flex-start"
          p={4}
          w="100%"
        >
          {/* Survey Heading Input */}
          <Heading fontSize="md" fontWeight="bold" mb={0}>
            Heading
          </Heading>
          <Input
            placeholder="Enter survey heading"
            value={heading}
            onChange={(e) => setHeading(e.target.value)}
            w="50%"
            minWidth="300px"
          />
          {/* Survey Subtext Input */}
          <Heading fontSize="md" fontWeight="bold" mb={0}>
            Text
          </Heading>
          <Textarea
            placeholder="Enter subtext for the survey"
            value={subtext}
            onChange={(e) => setSubtext(e.target.value)}
            w="50%"
            minWidth="300px"
          />
          <Heading fontSize="md" fontWeight="bold" mb={0}>
            Questions
          </Heading>
          {/* Question type selector */}
          <HStack spacing={4} w="50%">
            <HStack spacing={4}>
              <Button
                bg={newQuestionType === "radio" ? "#286328" : "gray.300"}
                color={newQuestionType === "radio" ? "white" : "black"}
                onClick={() => setNewQuestionType("radio")}
                size="sm"
                _hover="none"
              >
                Radio Options
              </Button>
              <Button
                bg={newQuestionType === "text" ? "#286328" : "gray.300"}
                color={newQuestionType === "text" ? "white" : "black"}
                onClick={() => setNewQuestionType("text")}
                size="sm"
                _hover="none"
              >
                Text
              </Button>
              <Button
                bg={newQuestionType === "image" ? "#286328" : "gray.300"}
                color={newQuestionType === "image" ? "white" : "black"}
                onClick={() => setNewQuestionType("image")}
                size="sm"
                _hover="none"
              >
                Image
              </Button>
            </HStack>
            <Spacer />
            <Button
              border="1px solid"
              bg="#286328"
              borderColor="#286328"
              color="white"
              _hover="none"
              onClick={isEditing ? updateSurvey : saveSurvey}
              size="sm"
            >
              {isEditing ? "Update Survey" : "Save Survey"}
            </Button>
          </HStack>
          {/* Survey question input */}
          <HStack w="50%" minWidth="300px" justifyContent="center">
            <Input
              placeholder="Enter question label"
              value={newQuestionLabel}
              onChange={(e) => setNewQuestionLabel(e.target.value)}
            />
            <HStack>
              <Text fontSize="sm" mb={0}>Required</Text>
              <input
                type="checkbox"
                checked={newRequired}
                onChange={(e) => setNewRequired(e.target.checked)}
                style={{ width: "16px", height: "16px" }}
              />
            </HStack>
          </HStack>
          {/* Show radio options input if question type is 'radio' */}
          {newQuestionType === "radio" && (
            <VStack align="flex-start" spacing={2} w="50%">
              {newOptions.map((option, index) => (
                <HStack key={index} w="100%" minWidth="300px">
                  <Input
                    value={option}
                    placeholder={`Option ${index + 1}`}
                    onChange={(e) => handleOptionChange(e.target.value, index)}
                  />
                  <IconButton
                    icon={<AddIcon />}
                    onClick={() => addOption(index)}
                    size="sm"
                    bg="#286328"
                    _hover="none"
                    color="white"
                  />
                </HStack>
              ))}
            </VStack>
          )}
          <Button
            bg="#286328"
            color="white"
            _hover="none"
            onClick={addQuestion}
            size="sm"
          >
            Add Question
          </Button>
          {/* Feedback field input */}
          <Box w="50%" minWidth="300px">
            <Heading fontSize="md" fontWeight="bold" mb={0}>
              Feedback Field (optional)
            </Heading>
            <Input
              placeholder="General feedback label (e.g. 'General feedback')"
              value={feedbackField}
              onChange={(e) => setFeedbackField(e.target.value)}
            />
          </Box>
          {/* List of added questions */}
          <VStack align="flex-start" w="70%" minWidth="300px">
            {questions.length > 0 && <Text mb={0}>Current Survey:</Text>}
            {questions.map((question, index) => (
              <Box
                key={index}
                p={4}
                bg="white"
                w="100%"
                borderRadius="md"
                shadow="sm"
              >
                <HStack justify="space-between">
                  <Text>
                    {question.label} {question.required && <span style={{color: 'red'}}>*</span>}
                  </Text>
                  <IconButton
                    icon={<DeleteIcon />}
                    onClick={() => deleteQuestion(index)}
                    size="sm"
                  />
                </HStack>
                {question.type === "radio" && (
                  <RadioGroup>
                    <VStack align="flex-start">
                      {question.options && question.options.map((option, optIndex) => (
                        <Radio key={optIndex} value={option} isDisabled>
                          {option}
                        </Radio>
                      ))}
                    </VStack>
                  </RadioGroup>
                )}
                {question.type === "text" && (
                  <Textarea placeholder={question.label} isDisabled />
                )}
                {question.type === "image" && (
                  <Box border="1px dashed #ccc" p={2} borderRadius="md" w="100%">
                    <Text fontSize="sm" color="gray.500">Image upload field (preview only)</Text>
                  </Box>
                )}
              </Box>
            ))}
          </VStack>
        </VStack>
      </Box>
    </Box>
  );
};

export default AdminFeedbackSurvey;
